/**
 * Theme store — persists user preference (dark / light / system) across
 * all EURION frontends via localStorage.
 *
 * CSS custom properties are set on <html> so Tailwind utility classes
 * like `bg-[var(--nord-bg)]` work without any JS-in-CSS overhead.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'dark' | 'light' | 'system';

// ─── Nord colour palettes ─────────────────────────────────────────────────────

const DARK_VARS: Record<string, string> = {
  '--nord0':  '#2E3440',
  '--nord1':  '#3B4252',
  '--nord2':  '#434C5E',
  '--nord3':  '#4C566A',
  '--nord4':  '#D8DEE9',
  '--nord5':  '#E5E9F0',
  '--nord6':  '#ECEFF4',
  '--nord7':  '#8FBCBB',
  '--nord8':  '#88C0D0',
  '--nord9':  '#81A1C1',
  '--nord10': '#5E81AC',
  '--nord11': '#BF616A',
  '--nord12': '#D08770',
  '--nord13': '#EBCB8B',
  '--nord14': '#A3BE8C',
  '--nord15': '#B48EAD',
  // Semantic aliases
  '--nord-bg':       '#2E3440',
  '--nord-surface':  '#3B4252',
  '--nord-border':   '#4C566A',
  '--nord-text':     '#ECEFF4',
  '--nord-subtext':  '#D8DEE9',
  '--nord-accent':   '#88C0D0',
  '--nord-danger':   '#BF616A',
  '--nord-success':  '#A3BE8C',
  '--nord-warning':  '#EBCB8B',
};

const LIGHT_VARS: Record<string, string> = {
  '--nord0':  '#ECEFF4',
  '--nord1':  '#E5E9F0',
  '--nord2':  '#D8DEE9',
  '--nord3':  '#C8D2DF',
  '--nord4':  '#4C566A',
  '--nord5':  '#434C5E',
  '--nord6':  '#3B4252',
  '--nord7':  '#3D7A79',
  '--nord8':  '#2F5F8F',
  '--nord9':  '#3E6A9E',
  '--nord10': '#3E6A9E',
  '--nord11': '#A3434D',
  '--nord12': '#A4502F',
  '--nord13': '#9A4A07',
  '--nord14': '#3F6B34',
  '--nord15': '#7A4E9A',
  // Semantic aliases
  '--nord-bg':       '#ECEFF4',
  '--nord-surface':  '#E5E9F0',
  '--nord-border':   '#C8D2DF',
  '--nord-text':     '#2E3440',
  '--nord-subtext':  '#4C566A',
  '--nord-accent':   '#2F5F8F',
  '--nord-danger':   '#A3434D',
  '--nord-success':  '#3F6B34',
  '--nord-warning':  '#9A4A07',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSystemPreference(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyVars(vars: Record<string, string>) {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}

export function applyTheme(mode: ThemeMode) {
  const resolved = mode === 'system' ? getSystemPreference() : mode;
  document.documentElement.setAttribute('data-theme', resolved);
  applyVars(resolved === 'dark' ? DARK_VARS : LIGHT_VARS);
}

// ─── Zustand store ────────────────────────────────────────────────────────────

interface ThemeState {
  mode:      ThemeMode;
  resolved:  'dark' | 'light';
  setMode:   (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode:     'dark',
      resolved: 'dark',

      setMode(mode: ThemeMode) {
        const resolved = mode === 'system' ? getSystemPreference() : mode;
        applyTheme(mode);
        set({ mode, resolved });
      },
    }),
    {
      name:    'eurion-theme',
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.mode);
      },
    },
  ),
);

// Re-apply when system preference changes (only relevant for mode='system')
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { mode, setMode } = useThemeStore.getState();
    if (mode === 'system') setMode('system');
  });
}
